import { AddressLinkDropdownPreview } from '@Components/index';
import { LinkedFieldWithLayoutSettings } from '@Components/LinkedFieldWithLayoutSettings';
import { addressColumns } from '@Constants/TableColumns';
import {
    type ApiAddress,
    type ApiInvoice,
    EnumBookingCode,
    EnumFrontendLayoutSetting,
    type EnumInvoiceType,
    invoiceTypes,
    type LinkableField,
} from '@Model';
import { FrameEditAddress } from '@Modules/Stammdaten';
import {
    type AsyncStateUpdate,
    type Badge,
    Card,
    Grid,
    GridColumn,
    GridColumns,
    type IGuiBinder,
    isLinked,
    isNullOrEmpty,
    renderCurrency,
    Select,
    type SelectOption,
    useAutoConfirmed,
    useLinkedField,
    useLinkedObjectOverlay,
    useLinkProperty,
    useProp,
    useTranslate,
    DateInput,
} from '@synqo/frontend-component-library';
import { type ComponentProps, memo } from 'react';
import { FrameEditInvoice } from '../../FrameEditInvoice';

// @ai-generated 2026-09-18T07:55:12+00:00 by philipp.kammerer@synqony.com using Claude Opus 5

const bookingCodes: SelectOption<EnumBookingCode>[] = [
    { name: 'debtor', label: 'Debitorisch', value: EnumBookingCode.Debtor },
    { name: 'creditor', label: 'Kreditorisch', value: EnumBookingCode.Creditor },
];

interface IProps {
    binder: IGuiBinder<ApiInvoice>;
    baseApiObjectPath: string;
    requestUpdateState: AsyncStateUpdate<ApiInvoice>;

    stateBadges: ComponentProps<typeof Badge>[];

    relatedCancellation: LinkableField;
    totalNetPrice: number;
    totalGrossPrice: number;
}

export const InvoiceMainSection = memo(function InvoiceMainSection({
    binder,
    baseApiObjectPath,
    requestUpdateState,
    stateBadges,
    relatedCancellation,
    totalNetPrice,
    totalGrossPrice,
}: IProps) {
    const i18n = useTranslate('');

    const linkCustomer = useLinkedField<ApiInvoice, ApiAddress>(
        baseApiObjectPath,
        'customer',
        FrameEditAddress,
        true,
        requestUpdateState,
    );

    const { onClick: onClickRelatedCancellation } = useLinkedObjectOverlay({
        Renderer: FrameEditInvoice,
        canAdd: false,
    });

    const typeField = useAutoConfirmed<ApiInvoice, EnumInvoiceType>(binder, 'type');
    const bookingCodeField = useAutoConfirmed<ApiInvoice, EnumBookingCode>(binder, 'booking_code');
    const dateField = useProp(binder, 'date');
    const bookingDateField = useProp(binder, 'booking_date');
    const customerField = useLinkProperty(binder, linkCustomer.linkApi);

    return (
        <Card
            name="maindata"
            gap="sm"
            border="withoutBorder"
            label="Allgemeine Informationen"
            badges={[
                ...stateBadges,
                !isNullOrEmpty(totalNetPrice?.toString()) && {
                    children: `Netto ${renderCurrency(totalNetPrice)}`,
                    variant: 'success',
                },
                !isNullOrEmpty(totalGrossPrice?.toString()) && {
                    children: `Brutto ${renderCurrency(totalGrossPrice)}`,
                    variant: 'success',
                },
                isLinked(relatedCancellation) && {
                    children: `Storniert durch Nr. ${relatedCancellation.display_string}`,
                    variant: 'danger',
                    onClick: () => {
                        onClickRelatedCancellation(relatedCancellation.foreign_key ?? '');
                    },
                },
            ]}
        >
            <Grid>
                <GridColumns spans={[6, 6]}>
                    <Select key="invoice_type" items={invoiceTypes} {...typeField} label={i18n('Dokumentenart')} />
                    <Select items={bookingCodes} {...bookingCodeField} label={i18n('Buchungs-Kennzeichnung')} />
                </GridColumns>
                <GridColumns spans={[6, 6]}>
                    <DateInput type="day" {...dateField} label={i18n('Rechnungsdatum')} />
                    <DateInput type="day" {...bookingDateField} label={i18n('Buchungsdatum')} />
                </GridColumns>
                <GridColumn span={12}>
                    <LinkedFieldWithLayoutSettings<ApiAddress>
                        layoutSettingsContext={EnumFrontendLayoutSetting.AddressesTableLayout}
                        {...customerField}
                        {...linkCustomer.events}
                        columns={addressColumns}
                        label={i18n('Kunde')}
                        dropdownPreview={AddressLinkDropdownPreview}
                    />
                </GridColumn>
            </Grid>
        </Card>
    );
});
